// How a Social Security Number reads once it's recorded (OLIO-2586).
//
// The full number is shown in exactly one place — the entry form, where someone has to be able to
// read what they're confirming or correcting. Every read-only surface uses this instead: the
// extraction card and the referral header today, and anywhere else it turns up later.
//
// The format lives here alone so changing it is one edit rather than a hunt.

const LAST_FOUR = 4;

// A complete Social Security Number, in digits.
export const SSN_LENGTH = 9;

// What gets STORED, and what the form's state holds: digits only, capped at nine.
//
// Digits rather than the dashed form on purpose. Extraction is told to write "digits only"
// (AttributeExtraction::SSN_SCHEMA), so a form that stored 123-45-6789 would put two shapes of the
// same number in the store — and on edit the dashed field would diff against the stored digits,
// rewrite the SSN on every save, and re-run every step that reads it. Reading a stored value through
// this keeps both sides of that comparison in one shape.
export function ssnDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '').slice(0, SSN_LENGTH);
}

// What gets DISPLAYED in the entry field: the dashed grouping, filled in as it's typed.
// The field exists so someone can check a number against the packet, and 123-45-6789 is the shape
// it's printed in — nine undelimited digits are meaningfully harder to compare.
export function formatSsn(value: string | null | undefined): string {
  const digits = ssnDigits(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;

  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

// Started but not finished. Empty stays valid — the number is optional, and a packet that doesn't
// state one is the common case. Only a half-entered number is worth stopping, and only when a person
// typed it: every masked surface renders a partial as ***-**-1234, exactly like a whole one, so it
// reads as a number on file while being unusable as one.
//
// Nothing consumes patient_ssn beyond display today, so this is about not recording a half-truth
// rather than about a broken lookup. A referral can also arrive holding a partial from a packet that
// printed the number masked — that one is left alone; see ssnEdited in ReferralModal.
export function isPartialSsn(value: string | null | undefined): boolean {
  const { length } = ssnDigits(value);

  return length > 0 && length < SSN_LENGTH;
}

// "***-**-1234" per the mock. Digits are pulled out first because a packet may state it as
// 123-45-6789 or 123456789 and both should mask identically. Anything too short to have a last
// four is masked whole rather than partially revealed — a 3-digit fragment showing in full is
// worse than a row that reads as redacted.
export function maskSsn(value: string | null | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length < LAST_FOUR) return '***-**-****';

  return `***-**-${digits.slice(-LAST_FOUR)}`;
}
